/**
 * Сокращает ФИО до фамилии с инициалами: «Иванов Иван Иванович» → «Иванов И. И.».
 * Без отчества — один инициал, одно слово или пустая строка возвращаются как есть.
 */
export const getAbbreviatedName = (fullName: string): string => {
  if (!fullName) return '';
  const parts = fullName.split(' ');
  if (parts.length < 2) return fullName;

  const lastName = parts[0];
  const initials = parts
    .slice(1, 3)
    .map((part) => (part[0] ? `${part[0]}.` : ''))
    .filter(Boolean)
    .join(' ');
  return `${lastName} ${initials}`.trim();
};

/**
 * Инициалы для аватара: первые буквы двух первых слов ФИО в верхнем регистре.
 * «Танкаев Азамат Русланович» → «ТА», «Азамат» → «А»; лишние пробелы игнорируются.
 */
export const getInitials = (fullName: string): string =>
  fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

type TNamePart = 'firstName' | 'lastName' | 'middleName';

interface IPersonNames {
  firstName?: string | null;
  lastName?: string | null;
  middleName?: string | null;
}

const NAME_PATTERNS = {
  last_first_middle: ['lastName', 'firstName', 'middleName'],
  first_middle_last: ['firstName', 'middleName', 'lastName'],
  first_last_middle: ['firstName', 'lastName', 'middleName'],
  first_last: ['firstName', 'lastName']
} satisfies Record<string, TNamePart[]>;

/**
 * Полное имя человека из частей в порядке `pattern` (по умолчанию «Фамилия Имя Отчество»).
 * Пустые и скрытые (`hide`) части пропускаются; если не осталось ни одной — `fallback`.
 */
export const getUserFullName = (
  names: IPersonNames | null | undefined,
  config?: {
    pattern?: keyof typeof NAME_PATTERNS;
    hide?: Partial<Record<TNamePart, boolean>>;
    fallback?: string;
  }
): string => {
  const { pattern = 'last_first_middle', hide, fallback = '' } = config ?? {};

  const result = NAME_PATTERNS[pattern]
    .filter((part) => !hide?.[part])
    .map((part) => names?.[part]?.trim())
    .filter(Boolean)
    .join(' ');

  return result || fallback;
};
